import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Table,
  Avatar,
  Tag,
  Empty,
  Spin,
  Button,
  Space,
  Typography,
  Modal,
  Dropdown,
  Select,
  Alert,
  Input,
  DatePicker,
  Checkbox
} from 'antd';
import {
  PieChartOutlined,
  CalendarOutlined,
  GiftOutlined,
  WalletOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
  MoreOutlined,
  EditOutlined,
  DownOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, ActionButtons } from '../../utils/tableFilters';
import { formatMoney, formatNumberTR } from '../../utils/currency';
import { trLower, trIncludes } from '../../utils/trText';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import FormikForm from '../form/FormikForm';
import { usePersonelFoto } from './PersonelFotoKirp';
import { message } from 'antd';
import './ik.css';
import '../fatura/FaturaPage.css';
dayjs.locale('tr');
ChartJS.register(ArcElement, Tooltip, Legend);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const PP_BASE_URL = 'https://dosya.atmturkiye.com';

const BUCKET_NAME = 'atmpoint';
const FOLDER_PATH = 'personel';

const generateProfileUrl = (userId) => {
  if (!userId) return null;
  return `${PP_BASE_URL}/${BUCKET_NAME}/${FOLDER_PATH}%2F${userId}.jpg`;
};

const getFilterDropdown = (column, darkMode, handleAdvancedFilterChange, clearColumnFilter, filteredInfo) => {
  if (!column || !column.filters) return null;
  const currentFilter = filteredInfo[column.key] || {};
  const condition = currentFilter.condition || "choose";

  return (
    <div style={{ padding: 16, minWidth: 300 }}>
      <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : 'var(--color-bg)', border: `1px solid ${darkMode ? 'var(--dark-border)' : 'var(--color-theme)'}`, padding: 12 }}>
        <div>
          <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
            Filtre Şartı
          </Text>
          <Select
            size="small"
            value={condition}
            onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
            style={{ width: '100%' }}
            className={darkMode ? 'dark-select' : ''}
          >
            {column.filters.map((filter) => (
              <Option key={filter.value} value={filter.value}>
                {filter.text}
              </Option>
            ))}
          </Select>
        </div>

        {column.isString && condition === "select" && column.uniqueValues && (
          <div>
            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
              Değer Seç
            </Text>
            <Select
              size="small"
              value={currentFilter.value || ""}
              onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
              style={{ width: '100%' }}
              placeholder="Seçiniz"
              className={darkMode ? 'dark-select' : ''}
            >
              <Option value="">Seçiniz</Option>
              {column.uniqueValues.map((val) => (
                <Option key={val} value={val}>
                  {val}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {column.isNumber && condition !== "choose" && condition !== "between" && (
          <div>
            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
              Değer
            </Text>
            <Input
              size="small"
              type="number"
              placeholder="Değer giriniz"
              value={currentFilter.value || ""}
              onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
              style={{ width: '100%' }}
              className={darkMode ? 'dark-input' : ''}
            />
          </div>
        )}

        {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
          <div>
            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
              Değer
            </Text>
            <Input
              size="small"
              placeholder="Değer giriniz"
              value={currentFilter.value || ""}
              onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
              style={{ width: '100%' }}
              className={darkMode ? 'dark-input' : ''}
            />
          </div>
        )}

        <Button
          size="small"
          onClick={() => clearColumnFilter(column.key)}
          style={{ marginTop: 8, width: '100%', borderColor: darkMode ? 'var(--dark-border)' : 'var(--color-theme)', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}
          className={darkMode ? 'dark-button' : ''}
        >
          Filtreyi Sıfırla
        </Button>
      </Space>
    </div>
  );
};

const IKPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  // 03.11.2026: personel fotoğrafı — yalnız İK + BT (Personel Listesi satırında "Fotoğraf")
  const { yetkili: fotoYetkisi, sec: fotoSec, pencere: fotoPenceresi } = usePersonelFoto();
  const [loading, setLoading] = useState(true);
  const [chartIndex, setChartIndex] = useState(0);
  const [firmaData, setFirmaData] = useState({ labels: [], data: [] });
  const [grupData, setGrupData] = useState({ labels: [], data: [] });
  const [cinsData, setCinsData] = useState({ labels: [], data: [] });
  const [yasData, setYasData] = useState({ labels: [], data: [] });
  const [holidays, setHolidays] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [advanceRequests, setAdvanceRequests] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState('ik-anasayfa');
  const [leaveRequestsModalOpen, setLeaveRequestsModalOpen] = useState(false);
  const [upcomingLeavesModalOpen, setUpcomingLeavesModalOpen] = useState(false);
  const [advanceRequestsModalOpen, setAdvanceRequestsModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 25
  });
  const [filters, setFilters] = useState({
    dateRange: null,
    search: ''
  });
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [showData, setShowData] = useState([]);
  const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [formTableName, setFormTableName] = useState('');
  const [formUniqueField, setFormUniqueField] = useState('');
  const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
  const [currentFormExtraData, setCurrentFormExtraData] = useState({});
  const [currentFormFunction, setCurrentFormFunction] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [modalButtonType, setModalButtonType] = useState('main-table');
  const [izinModalOpen, setIzinModalOpen] = useState(false);
  const [izinModalData, setIzinModalData] = useState(null);
  const [izinModalLoading, setIzinModalLoading] = useState(false);
  const [izinModalError, setIzinModalError] = useState(null);
  const [currentIzinId, setCurrentIzinId] = useState(null);

  const chartTypes = [
    { key: 'firma', label: 'Firma', data: firmaData, colors: ['#fa4747', '#23b57a', '#0f75bc', '#afcb08', '#EAD57D', '#3669B2'] },
    { key: 'grup', label: 'Departman', data: grupData, colors: ['#9fd708', '#6c8318', '#97779b', '#88b6f1', '#01fdfb', '#b06b35', '#f29f44', '#e09ff1', '#782609', '#a18da2', '#4062b2', '#74d17d', '#3b66dd', '#21fe22', '#0fba3e', '#a9e327', '#5aa510', '#de5d3a', '#ad6ce6', '#1d64b1', '#a2a2f2', '#be4687', '#d8b1ae'] },
    { key: 'cins', label: 'Cinsiyet', data: cinsData, colors: ['#87a8eb', '#eb87a4'] },
    { key: 'yas', label: 'Yaş', data: yasData, colors: ['#73db5b', '#b2db5b', '#dbd35b', '#db945b', '#db645b', '#ccc'] }
  ];

  const currentChart = chartTypes[chartIndex];

  const ikMenus = [
    {
      key: 'ik-anasayfa',
      label: 'Anasayfa',
      route: '/ik'
    },
    {
      key: 'personel-liste',
      label: 'Personel Listesi',
      sqlKey: 'personel',
      actionButtons: [
        {
          key: 'view',
          label: 'Detay',
          type: 'default',
          onClick: (record) => {
            navigate(`/ik/personel/${record.key_personel}`);
          }
        },
        {
          key: 'edit',
          label: 'Düzenle',
          type: 'default',
          onClick: (record) => {
            const extraData = {
              eklenme_tarihi: new Date().toISOString().split('T')[0],
            };
            handleOpenForm(173, 'Personel Düzenle', 'kullanicilar', 'id', record.key_personel, {extraData}, 'Insert');
          }
        },
        {
          key: 'add',
          label: 'Ekle',
          type: 'default',
          onClick: (record) => {
            const extraData = {
              ekleyen: user?.kullanici_no,
              kullanici: user?.kullanici_no,
              root: 'ozlukdosyalar',
              dir: 'ozlukdosyalar',
              eklenme_tarihi: new Date().toISOString().split('T')[0],
            };
            handleOpenForm(192, 'Özlük Belgeleri', 'ozluk', 'id', record.key_personel, { extraData }, 'Insert');
          }
        },
        ...(fotoYetkisi ? [{
          key: 'foto',
          label: 'Fotoğraf',
          type: 'default',
          onClick: (record) => fotoSec(record.key_personel)
        }] : [])
      ]
    },
    {
      key: 'izin-liste',
      label: 'İzin Listesi',
      sqlKey: 'izin-talepleri',
      actionButtons: [
          {
            key: 'islem',
            label: 'İşlem',
            type: 'default',
            onClick: (record) => {
              handleOpenIzinModal(record);
            }
          },
          {
            key: 'detay',
            label: 'Detay',
            type: 'default',
            onClick: (record) => {
              const id = record.key_izinim ?? record.key_izin;
              if (id != null) navigate(`/ik/dilekce/${id}`);
              else message.warning('İzin kaydı bulunamadı');
            }
          }
        ]
    },
    {
      key: 'zimmet-liste',
      label: 'Zimmet Listesi',
      sqlKey: 'zimmet-liste',
      actionButtons: {
        'KEY_ZIMMET': [
          {
            key: 'add',
            label: 'Teslimat Ekle',
            type: 'default',
            icon: 'PlusOutlined',
            onClick: (record) => openFormForRecord(record, 'No', 100, 'Zimmet Teslim Formu', 'zimmet'),
          },
          {
            key: 'edit',
            label: 'Düzenle',
            type: 'default',
            icon: 'EditOutlined',
            onClick: (record) => {
              const extraData = {
                ekleyen: user?.kullanici_no,
                eklenmeTarihi: new Date().toISOString().split('T')[0],
              };
              openFormForRecord(record, 'No', 98, 'Zimmet Düzenle', 'zimmet', extraData, 'Update');
            },
          },
          {
            key: 'view',
            label: 'Zimmet Tutanak',
            type: 'default',
            icon: 'EyeOutlined',
            onClick: (record) => {
              const kul_id = record.kul_no;
              if (kul_id) navigate(`/ik/zimmet-tutanak/${kul_id}`);
              else message.error('Zimmet ID bulunamadı');
            },
          },
        ],
        'KEY_DEMIRBAS_ZIMMET': [
          {
            key: 'view',
            label: 'Aç',
            type: 'default',
            icon: 'EyeOutlined',
            onClick: (record) => {
              handleOpenDetailModal(record, null, 'main-table');
            }
          }
        ]
      }
    },
    {
      key: 'pozisyonlar',
      label: 'Pozisyonlar',
      sqlKey: 'pozisyon',
      actionButtons: [
        {
          key: 'view',
          label: 'Düzenle',
          type: 'default',
          onClick: (record) => {
            const extraData = {
              ekleyen: user?.kullanici_no,
              eklenmeTarihi: new Date().toISOString().split('T')[0],
            };
            handleOpenForm(28, 'Pozisyon Detayı', 'ik_pozisyon', 'id', record.id, {extraData}, 'Insert');
          }
        }
      ]
    },
    {
      key: 'organizasyon-semasi',
      label: 'Organizasyon Şeması',
      route: '/organizasyon-semasi'
    }
  ];

  const loadTableData = useCallback(async (sqlKey) => {
    setTableLoading(true);
    setTableError(null);
    try {
      const url = `${API_BASE_URL}/list/execute/${sqlKey}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error - Status: ${response.status}, Response:`, errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const allData = await response.json();
      if (!Array.isArray(allData)) {
        console.warn('API response is not an array:', allData);
        setTableError('Geçersiz veri formatı - API array döndürmüyor');
        return;
      }

      if (allData.length > 0) {
        const firstRecord = allData[0];
        const dynamicCols = Object.keys(firstRecord)
          .filter((key) => !key.toLowerCase().includes('_row_style'))
          .map((key) => {
            const firstValue = firstRecord[key];
            const isDateColumn = key.toLowerCase().includes('tarih') ||
              key.toLowerCase().includes('tar') ||
              key.toLowerCase().includes('date');
            const isNumber = typeof firstValue === "number" && !isNaN(firstValue);
            const uniqueValues = [...new Set(allData.map((item) => item[key]))].filter(
              (val) => val !== undefined && val !== null && val !== ""
            );

            let filters = [];
            if (isNumber) {
              filters = getNumericFilters();
            } else if (isDateColumn) {
              filters = getDateFilters();
            } else {
              filters = getStringFilters();
            }

            return {
              key: key,
              title: key,
              dataIndex: key,
              isDateColumn,
              isNumber,
              isString: !isNumber && !isDateColumn,
              uniqueValues,
              sortable: true,
              filters,
              sorter: true,
              render: (text) => {
                if (text === null || text === undefined || text === '') return '-';
                if (isDateColumn && text) {
                  const date = dayjs(text);
                  if (date.isValid()) {
                    return date.format('DD.MM.YYYY');
                  }
                }
                if (isNumber && text !== null && text !== undefined) {
                  return formatNumberTR(text);
                }
                return String(text);
              }
            };
          });
        setTableColumns(dynamicCols);
      }

      setTableData(allData || []);
      setShowData(allData || []);
      setPagination(prev => ({
        ...prev,
        total: allData?.length || 0,
        current: 1
      }));
    } catch (err) {
      console.error('Data load error:', err);
      setTableError('Veri yüklenirken hata oluştu: ' + err.message);
    } finally {
      setTableLoading(false);
    }
  }, [getAuthHeaders]);

  const handleMenuChange = (key) => {
    setSelectedMenu(key);
    const selectedMenuData = ikMenus.find(menu => menu.key === key);
    if (!selectedMenuData) return;

    if (selectedMenuData.route) {
      navigate(selectedMenuData.route);
    } else if (selectedMenuData.sqlKey) {
      loadTableData(selectedMenuData.sqlKey);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleAdvancedFilterChange = (columnKey, valueType, value) => {
    setFilteredInfo((prev) => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        [valueType]: value,
      },
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const clearColumnFilter = (columnKey) => {
    setFilteredInfo((prev) => {
      const newFilteredInfo = { ...prev };
      delete newFilteredInfo[columnKey];
      return newFilteredInfo;
    });
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const clearAllFilters = useCallback(() => {
    setFilteredInfo({});
    setFilters({
      dateRange: null,
      search: ''
    });
    setSortedInfo({});
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  }, []);

  const saveSelectedColumnsToStorage = useCallback((columnKeys) => {
    try {
      const storageKey = `ik_selected_columns_${selectedMenu}`;
      localStorage.setItem(storageKey, JSON.stringify(columnKeys));
    } catch (error) {
      console.error('Error saving selected columns to storage:', error);
    }
  }, [selectedMenu]);

  useEffect(() => {
    if (tableColumns.length > 0) {
      try {
        const storageKey = `ik_selected_columns_${selectedMenu}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          const validColumns = parsed.filter(key => tableColumns.some(col => col.key === key));
          if (validColumns.length > 0) {
            setSelectedColumns(validColumns);
          } else {
            const allColumnKeys = tableColumns.map(col => col.key);
            setSelectedColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys);
          }
        } else {
          const allColumnKeys = tableColumns.map(col => col.key);
          setSelectedColumns(allColumnKeys);
          saveSelectedColumnsToStorage(allColumnKeys);
        }
      } catch (error) {
        console.error('Error loading selected columns from storage:', error);
        const allColumnKeys = tableColumns.map(col => col.key);
        setSelectedColumns(allColumnKeys);
      }
    }
  }, [tableColumns, saveSelectedColumnsToStorage, selectedMenu]);

  const handleSort = (columnKey) => {
    const column = tableColumns.find((col) => col.key === columnKey);
    if (!column) return;

    let newOrder = "asc";
    if (sortedInfo.columnKey === columnKey) {
      if (sortedInfo.order === "asc") {
        newOrder = "desc";
      } else if (sortedInfo.order === "desc") {
        newOrder = null;
      }
    }

    setSortedInfo({
      columnKey: newOrder ? columnKey : null,
      order: newOrder,
    });
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = [...tableData];

    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const filterStartDate = filters.dateRange[0].startOf('day');
      const filterEndDate = filters.dateRange[1].endOf('day');
      
      filtered = filtered.filter(record => {
        const dateKeys = Object.keys(record).filter(key =>
          key.toLowerCase().includes('tarih') ||
          key.toLowerCase().includes('tar') ||
          key.toLowerCase().includes('date')
        );
        
        if (dateKeys.length === 0) return true;
        
        return dateKeys.some(dateKey => {
          const dateValue = record[dateKey];
          if (!dateValue) return false;
          const date = dayjs(dateValue);
          if (date.isValid()) {
            return date.isBetween(filterStartDate, filterEndDate, null, '[]');
          }
          return false;
        });
      });
    }

    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.trim();
      filtered = filtered.filter(record => {
        return Object.values(record).some(value => {
          if (value === null || value === undefined) return false;
          return trIncludes(String(value), searchTerm);
        });
      });
    }

    Object.keys(filteredInfo).forEach(columnKey => {
      const filter = filteredInfo[columnKey];
      if (!filter || !filter.condition || filter.condition === "choose") return;
      const column = tableColumns.find(col => col.key === columnKey);
      if (!column) return;

      filtered = filtered.filter(record => {
        const value = record[columnKey];
        const filterValue = filter.value;
        if (column.isNumber) {
          const numValue = parseFloat(value);
          const numFilter = parseFloat(filterValue);
          if (isNaN(numValue) || isNaN(numFilter)) return false;
          switch (filter.condition) {
            case "equals": return numValue === numFilter;
            case "lt": return numValue < numFilter;
            case "gt": return numValue > numFilter;
            case "lte": return numValue <= numFilter;
            case "gte": return numValue >= numFilter;
            case "notEquals": return numValue !== numFilter;
            default: return true;
          }
        } else if (column.isString) {
          const strValue = trLower(value || '');
          const strFilter = trLower(filterValue || '');
          switch (filter.condition) {
            case "equals": return strValue === strFilter;
            case "contains": return strValue.includes(strFilter);
            case "notContains": return !strValue.includes(strFilter);
            case "startsWith": return strValue.startsWith(strFilter);
            case "endsWith": return strValue.endsWith(strFilter);
            case "empty": return !value || value === '';
            case "notEmpty": return value && value !== '';
            case "select": return strValue === strFilter;
            default: return true;
          }
        }
        return true;
      });
    });

    if (sortedInfo.columnKey && sortedInfo.order) {
      const column = tableColumns.find(col => col.key === sortedInfo.columnKey);
      if (column) {
        filtered.sort((a, b) => {
          const aVal = a[sortedInfo.columnKey];
          const bVal = b[sortedInfo.columnKey];
          if (column.isNumber) {
            const aNum = parseFloat(aVal) || 0;
            const bNum = parseFloat(bVal) || 0;
            return sortedInfo.order === "asc" ? aNum - bNum : bNum - aNum;
          } else if (column.isDateColumn) {
            const aDate = dayjs(aVal);
            const bDate = dayjs(bVal);
            if (!aDate.isValid() || !bDate.isValid()) return 0;
            return sortedInfo.order === "asc" ? aDate - bDate : bDate - aDate;
          } else {
            const aStr = String(aVal || '').toLowerCase();
            const bStr = String(bVal || '').toLowerCase();
            return sortedInfo.order === "asc"
              ? aStr.localeCompare(bStr, 'tr')
              : bStr.localeCompare(aStr, 'tr');
          }
        });
      }
    }

    return filtered;
  }, [tableData, filters, filteredInfo, sortedInfo, tableColumns]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEmployeeDistribution(),
        fetchHolidays(),
        fetchLeaveRequests(),
        fetchUpcomingLeaves(),
        fetchBirthdays(),
        fetchAdvanceRequests(),
        fetchEvents()
      ]);
    } catch (error) {
      console.error('Error fetching HR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDistribution = async () => {
    try {
      const endpoints = ['firma', 'grup', 'cinsiyet', 'yas'];
      const results = await Promise.allSettled(
        endpoints.map((path) =>
          axios.get(`${API_BASE_URL}/ik/employee-distribution/${path}`, { headers: getAuthHeaders() })
        )
      );
      const setters = [setFirmaData, setGrupData, setCinsData, setYasData];
      results.forEach((res, i) => {
        if (res.status === 'fulfilled' && res.value?.data) {
          const data = res.value.data;
          setters[i]({ labels: data.labels || [], data: data.data || [] });
        }
      });
    } catch (error) {
      console.error('Error fetching employee distribution:', error);
    }
  };

  const fetchIkList = useCallback(async (endpoint, setter, params = { limit: 5 }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: getAuthHeaders(),
        params,
      });
      if (response.data && Array.isArray(response.data)) setter(response.data);
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
    }
  }, [getAuthHeaders]);

  const fetchHolidays = useCallback(() => fetchIkList('/ik/holidays', setHolidays), [fetchIkList]);
  const fetchLeaveRequests = useCallback(() => fetchIkList('/ik/leave-requests', setLeaveRequests), [fetchIkList]);
  const fetchUpcomingLeaves = useCallback(() => fetchIkList('/ik/upcoming-leaves', setUpcomingLeaves), [fetchIkList]);

  const fetchBirthdays = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/list/yaklasan-dg`, {
        headers: getAuthHeaders(),
        params: { limit: 5 }
      });
      if (response.data && Array.isArray(response.data)) {
        const transformed = response.data.map((item, index) => {
          let dg_kalan = item.dg_kalan;
          if (dg_kalan === null || dg_kalan === undefined) {
            dg_kalan = 0;
          } else if (typeof dg_kalan === 'string') {
            dg_kalan = parseInt(dg_kalan);
          } else {
            dg_kalan = parseInt(dg_kalan);
          }

          return {
            id: item.kullanici_no || index,
            pp: item.kullanici_no,
            personel_adi: item.ad_soyad || 'Bilinmiyor',
            'Gün': item.dogum_gun,
            'Ay_ad': item.dogum_ay,
            dg_kalan: dg_kalan
          };
        });
        setBirthdays(transformed);
      }
    } catch (error) {
      console.error('Error fetching birthdays:', error);
    }
  };

  const fetchAdvanceRequests = useCallback(() => fetchIkList('/ik/odeme-talebi', setAdvanceRequests), [fetchIkList]);
  const getChartData = () => {
    if (!currentChart.data.labels.length) return null;

    return {
      labels: currentChart.data.labels,
      datasets: [{
        data: currentChart.data.data,
        backgroundColor: currentChart.colors.slice(0, currentChart.data.labels.length),
        borderWidth: 2,
        borderColor: darkMode ? '#1f1f1f' : '#ffffff'
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 8,
          font: {
            size: 11
          },
          color: darkMode ? '#ffffff' : '#000000',
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: darkMode ? '#ffffff' : '#000000',
        bodyColor: darkMode ? '#ffffff' : '#000000',
        borderColor: darkMode ? '#333' : '#ddd',
        borderWidth: 1
      }
    },
    cutout: '60%'
  };

  const getPersonelColumn = (icon = null, width = 150) => ({
    title: 'Personel',
    dataIndex: 'pp',
    key: 'pp',
    render: (userId, record) => (
      <Space>
        <Avatar src={generateProfileUrl(userId)} icon={icon} size="small" />
        <Text>{record.personel_adi || 'Bilinmiyor'}</Text>
      </Space>
    ),
    width,
  });

  const holidaysColumns = [
    {
      title: 'Tarih',
      dataIndex: 'tarih',
      key: 'tarih',
      render: (text) => {
        const date = dayjs(text);
        const days = ['Pzr.', 'Pzt.', 'Sal.', 'Çar.', 'Per.', 'Cum.', 'Cmt.'];
        return `${date.format('DD.MM.YYYY')} ${days[date.day()]}`;
      },
      width: 120
    },
    {
      title: 'Tatil',
      dataIndex: 'ad',
      key: 'ad',
      render: (text, record) => {
        const duration = record.yarim_gun ? '(Yarım Gün)' :
          record.ad?.includes('Ramazan Bayramı') ? '(3 Gün)' :
            record.ad?.includes('Kurban Bayramı') ? '(4 Gün)' : '(1 Gün)';
        return `${text} ${duration}`;
      }
    }
  ];

  const leaveRequestsColumns = [
    getPersonelColumn(null, 150),
    {
      title: 'Baş. Tarihi',
      dataIndex: 'baslangicTarihi',
      key: 'baslangicTarihi',
      render: (text) => dayjs(text).format('DD.MM.YYYY'),
      width: 110
    },
    {
      title: 'Süre',
      dataIndex: 'izin_suresi',
      key: 'izin_suresi',
      width: 80
    },
    {
      title: 'Durum',
      dataIndex: 'onay',
      key: 'onay',
      render: (onay) => (
        <Tag color={onay === 1 ? 'green' : 'orange'}>
          {onay === 1 ? 'Onaylandı' : 'Beklemede'}
        </Tag>
      ),
      width: 100
    },
    {
      title: 'İşlem',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="default"
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenIzinModal(record);
          }}
        >
          Görüntüle
        </Button>
      )
    }
  ];

  const birthdaysColumns = [
    getPersonelColumn(<GiftOutlined />, 250),
    {
      title: 'Gün',
      dataIndex: 'Gün',
      key: 'Gün',
      render: (day, record) => {
        const dg_kalan = parseInt(record.dg_kalan);
        if (dg_kalan === 0) return `${day}`;
        if (dg_kalan === 1) return `${day}`;
        return day;
      },
      width: 20
    },
    {
      title: 'Ay',
      dataIndex: 'Ay_ad',
      key: 'Ay_ad',
      render: (month) => {
        const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
          'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
        return months[month - 1] || month;
      },
      width: 40
    },
    {
      title: 'Kalan Gün',
      dataIndex: 'dg_kalan',
      key: 'dg_kalan',
      render: (days) => {
        const dg_kalan = parseInt(days);
        if (dg_kalan === 0) return <Tag color="red">Bugün</Tag>;
        if (dg_kalan === 1) return <Tag color="orange">Yarın</Tag>;
        return <Text>{dg_kalan} gün</Text>;
      },
      width: 20
    }
  ];

  const upcomingLeavesColumns = [
    {
      title: 'Personel',
      dataIndex: 'pp',
      key: 'pp',
      render: (userId, record) => (
        <Space>
          <Avatar
            src={generateProfileUrl(userId)}
            icon={<CalendarOutlined />}
            size="small"
          />
          <Text>{record.personel_adi || 'Bilinmiyor'}</Text>
        </Space>
      ),
      width: 150
    },
    {
      title: 'Baş. Tarihi',
      dataIndex: 'baslangicTarihi',
      key: 'baslangicTarihi',
      render: (text) => dayjs(text).format('DD.MM.YYYY'),
      width: 110
    },
    {
      title: 'Süre',
      dataIndex: 'sure',
      key: 'sure',
      width: 80
    }
  ];

  const advanceRequestsColumns = [
    getPersonelColumn(<WalletOutlined />, 150),
    {
      title: 'Tutar',
      dataIndex: 'tutar',
      key: 'tutar',
      render: (tutar) => formatMoney(tutar || 0),
      width: 120
    },
    {
      title: 'Tarih',
      dataIndex: 'tarih',
      key: 'tarih',
      render: (text) => dayjs(text).format('DD.MM.YYYY'),
      width: 110
    }
  ];

  const nextChart = () => {
    setChartIndex((prev) => (prev + 1) % chartTypes.length);
  };

  const prevChart = () => {
    setChartIndex((prev) => (prev - 1 + chartTypes.length) % chartTypes.length);
  };

  const selectedMenuData = ikMenus.find(menu => menu.key === selectedMenu);
  const showTable = selectedMenuData && selectedMenuData.sqlKey;
  const visibleColumns = useMemo(() => {
    if (!tableColumns || tableColumns.length === 0) {
      return [];
    }
    let cols = selectedColumns.length === 0
      ? [...tableColumns]
      : tableColumns.filter(col => selectedColumns.includes(col.key));
    if (selectedMenu === 'zimmet-liste') {
      const keyZimmet = cols.findIndex(c => (c.key || '').toUpperCase().replace(/\s+/g, '_') === 'KEY_ZIMMET' || (c.key || '').toLowerCase() === 'key_zimmet');
      cols = cols.filter(c => {
        const k = trLower(c.key || '').replace(/\s+/g, '_');
        return k !== 'key_demirbas_zimmet';
      });
    }
    if (selectedMenu === 'izin-liste') {
      cols = cols.filter(c => {
        const k = trLower(c.key || '').replace(/\s+/g, '_');
        return k !== 'key_izinim';
      });
    }
    return cols;
  }, [tableColumns, selectedColumns, selectedMenu]);

  const removeColumn = (columnKey) => {
    setSelectedColumns((prev) => {
      const newSelected = prev.filter((key) => key !== columnKey);
      saveSelectedColumnsToStorage(newSelected);
      return newSelected;
    });
  };

  const handleColumnSelectionChange = (checkedValues) => {
    setSelectedColumns(checkedValues);
    saveSelectedColumnsToStorage(checkedValues);
  };

  const handleSelectAllColumns = (checked) => {
    if (checked) {
      const allColumnKeys = tableColumns.map(col => col.key);
      setSelectedColumns(allColumnKeys);
      saveSelectedColumnsToStorage(allColumnKeys);
    } else {
      setSelectedColumns([]);
      saveSelectedColumnsToStorage([]);
    }
  };

  const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null, extraDataOrWrapper = null, formFunction = null) => {
    setModalTitle(title);
    setCurrentFormId(formId);
    setFormTableName(tableName);
    setFormUniqueField(uniqueField);
    setCurrentUniqueValue(uniqueValue);
    const extra = extraDataOrWrapper?.extraData != null ? extraDataOrWrapper.extraData : (extraDataOrWrapper || {});
    setCurrentFormExtraData(extra);
    setCurrentFormFunction(formFunction || null);
    setFormModalOpen(true);
  };

  const openFormForRecord = (record, idField, formId, title, tableName, extraData = null, formFunction = null) => {
    const id = record[idField];
    if (id != null) {
      handleOpenForm(formId, title, tableName, 'id', id, extraData ? { extraData } : null, formFunction);
    } else {
      message.error('ID bulunamadı');
    }
  };

  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setCurrentFormId(null);
    setFormTableName('');
    setFormUniqueField('');
    setCurrentFormExtraData({});
    setCurrentFormFunction(null);
    if (showTable) {
      const selectedMenuData = ikMenus.find(menu => menu.key === selectedMenu);
      if (selectedMenuData && selectedMenuData.sqlKey) {
        loadTableData(selectedMenuData.sqlKey);
      }
    }
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
  };

  const handleOpenDetailModal = async (record, firstChar = null, modalType = 'main-table') => {
    const id = record.key_demirbas_zimmet || record.id || record.No;
    if (!id) {
      message.error('ID bulunamadı');
      return;
    }

    const idString = String(id);
    setCurrentRecordId(idString);
    setDetailModalOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setModalButtonType(modalType);

    try {
      const charToUse = firstChar || idString.charAt(0).toLowerCase();
      let queryName = 'demirbas';
      let modalTitleText = 'Demirbaş Detayları';

      if (modalType === 'sub-table') {
        queryName = 'demirbas';
        modalTitleText = 'Demirbaş Detayları';
      } else {
        switch (charToUse) {
          case 'k':
            modalTitleText = 'Kredi Kartı Zimmet Detayları';
            queryName = 'zimmet-detail-k';
            break;
          case 'a':
            modalTitleText = 'Araç Zimmet Detayları';
            queryName = 'zimmet-detail-a';
            break;
          case 'd':
            modalTitleText = 'Demirbaş Zimmet Detayları';
            queryName = 'zimmet-detail-d';
            break;
          default:
            queryName = 'demirbas';
            modalTitleText = 'Demirbaş Detayları';
            break;
        }
      }

      let paramId = idString;
      if (modalType === 'sub-table' && queryName === 'demirbas') {
        paramId = record.demirbas_id || record.id || idString;
      }

      const response = await fetch(`${API_BASE_URL}/list/execute-query/${queryName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ params: { id: paramId } })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailData(Array.isArray(data) ? data : []);
      setModalTitle(modalTitleText);
    } catch (err) {
      console.error('Detail modal data load error:', err);
      setDetailError('Detay verileri yüklenirken hata oluştu: ' + err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailData([]);
    setDetailError(null);
    setCurrentRecordId(null);
    setModalButtonType('main-table');
  };

  const handleOpenIzinModal = async (record) => {
    const id = record.key_izinim ?? record.key_izin ?? record.id;
    if (id == null) {
      message.warning('İzin kaydı bulunamadı');
      return;
    }
    setCurrentIzinId(id);
    setIzinModalOpen(true);
    setIzinModalLoading(true);
    setIzinModalError(null);
    setIzinModalData(null);
    try {
      const response = await fetch(`${API_BASE_URL}/list/execute-query/profil-izin-detay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ params: { id: Number(id) } })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const row = Array.isArray(data) && data.length > 0 ? data[0] : (data || null);
      setIzinModalData(row);
    } catch (err) {
      console.error('İzin detay yükleme hatası:', err);
      setIzinModalError('İzin detayı yüklenirken hata oluştu: ' + err.message);
    } finally {
      setIzinModalLoading(false);
    }
  };

  const closeIzinModal = () => {
    setIzinModalOpen(false);
    setIzinModalData(null);
    setIzinModalError(null);
    setCurrentIzinId(null);
  };

  const formatIzinDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === null) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '-';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  };

  const handleIzinOnayla = async () => {
    if (currentIzinId == null) {
      message.warning('İzin kaydı bulunamadı');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/form/submit-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          table_name: 'ik_izin',
          form_data: { onay: 1 },
          function: 'Update',
          unique_field: 'id',
          unique_value: Number(currentIzinId),
          user_id: user?.kullanici_no
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.detail || result?.message || 'Güncelleme başarısız');
      message.success('İzin talebi onaylandı');
      closeIzinModal();
      const selectedMenuData = ikMenus.find(menu => menu.key === selectedMenu);
      if (selectedMenuData?.sqlKey) loadTableData(selectedMenuData.sqlKey);
    } catch (err) {
      console.error('İzin onaylama hatası:', err);
      message.error(err?.message || 'İzin onaylanırken hata oluştu');
    }
  };

  const handleIzinReddet = async () => {
    if (currentIzinId == null) {
      message.warning('İzin kaydı bulunamadı');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/form/submit-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          table_name: 'ik_izin',
          form_data: { onay: 0 },
          function: 'Update',
          unique_field: 'id',
          unique_value: Number(currentIzinId),
          user_id: user?.kullanici_no
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.detail || result?.message || 'Güncelleme başarısız');
      message.info('İzin talebi reddedildi');
      closeIzinModal();
      const selectedMenuData = ikMenus.find(menu => menu.key === selectedMenu);
      if (selectedMenuData?.sqlKey) loadTableData(selectedMenuData.sqlKey);
    } catch (err) {
      console.error('İzin reddetme hatası:', err);
      message.error(err?.message || 'İzin reddedilirken hata oluştu');
    }
  };

  const handleViewFile = (record) => {
    const id = record.file;
    if (!id) {
      message.error('ID bulunamadı');
      return;
    }
    const MINIO_BASE_URL = 'https://dosya.atmturkiye.com';
    const BUCKET_NAME = 'atmpoint';
    const pathSegments = id.split('/');
    const encodedSegments = pathSegments.map(segment => {
      return encodeURIComponent(segment);
    });
    const encodedPath = encodedSegments.join('/');
    const fileUrl = `${MINIO_BASE_URL}/${BUCKET_NAME}/${encodedPath}`;
    window.open(fileUrl, '_blank');
  };

  const handleEditFile = (record) => {
    const extraData = {
      ekleyen: user?.kullanici_no,
      eklenmeTarihi: new Date().toISOString().split('T')[0],
    };
    openFormForRecord(record, 'key_zimmet_edit', 98, 'Demirbaş Dosya Düzenle', 'zimmet', extraData, 'Update');
  };

  const handleAddDelivery = (record) => {
    openFormForRecord(record, 'key_zimmet_edit', 100, 'Zimmet Teslim Formu', 'zimmet');
  };

  const paginatedData = useMemo(() => {
    if (!showTable) return [];
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, pagination, showTable]);

  if (loading && !showTable) {
    return (
      <div className="ik-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={`ik-dashboard ${darkMode ? 'dark' : 'light'}`}>
      {fotoPenceresi}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8} md={6} lg={4}>
              <div className="ik-menu-selector">
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: '14px', color: darkMode ? 'var(--dark-text)' : undefined }}>
                    İK Menüsü
                  </Text>
                  <EditOutlined
                    style={{
                      fontSize: '12px',
                      cursor: 'pointer',
                      color: darkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)'
                    }}
                    onClick={() => {
                    }}
                  />
                </Space>
                <Select
                  placeholder="İK Menüsü Seçin"
                  value={selectedMenu}
                  onChange={handleMenuChange}
                  style={{ width: '100%' }}
                  suffixIcon={<DownOutlined />}
                  className="ik-menu-select"
                >
                  {ikMenus.map(menu => (
                    <Select.Option key={menu.key} value={menu.key}>
                      {menu.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>
        </Col>
        {showTable ? (
          <Col xs={24} sm={24} md={24} lg={24}>
            <div className="ik-table-view">
              <Card className="ik-card">
                {/* Filters Row */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16, marginTop: 0 }}>
                  <Col xs={24} sm={12} md={6} lg={4}>
                    <div className="filter-item">
                      <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                        Arama
                      </Text>
                      <Input
                        placeholder="Arama yapın..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                        allowClear
                        style={{
                          backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                          borderColor: darkMode ? 'var(--dark-border)' : undefined,
                          color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                        className={darkMode ? 'dark-input' : ''}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={18} lg={20}>
                    <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', height: '100%', gap: 8 }}>
                      <Dropdown
                        open={columnSelectDropdownOpen}
                        onOpenChange={setColumnSelectDropdownOpen}
                        trigger={['click']}
                        dropdownRender={() => (
                          <div style={{
                            padding: '2px',
                            minWidth: '250px',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                            border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`,
                            borderRadius: '6px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}>
                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}` }}>
                              <Checkbox
                                checked={selectedColumns.length === tableColumns.length && tableColumns.length > 0}
                                indeterminate={selectedColumns.length > 0 && selectedColumns.length < tableColumns.length}
                                onChange={(e) => handleSelectAllColumns(e.target.checked)}
                                style={{ color: darkMode ? 'var(--dark-text)' : undefined }}
                              >
                                <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                  Tümünü Seç
                                </Text>
                              </Checkbox>
                            </div>
                            <Checkbox.Group
                              value={selectedColumns}
                              onChange={handleColumnSelectionChange}
                              style={{ width: '100%' }}
                            >
                              <Space direction="vertical" style={{ width: '100%' }}>
                                {tableColumns.map(column => (
                                  <Checkbox
                                    key={column.key}
                                    value={column.key}
                                    style={{
                                      width: '100%',
                                      color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                  >
                                    {column.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </div>
                        )}
                      >
                        <Button
                          icon={<EyeOutlined />}
                          className={darkMode ? 'dark-button' : ''}
                          style={{
                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                            color: darkMode ? 'var(--dark-text)' : undefined
                          }}
                        >
                          Sütun Seç
                        </Button>
                      </Dropdown>
                      <Button
                        onClick={clearAllFilters}
                        icon={<ClearOutlined />}
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                          backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                          borderColor: darkMode ? 'var(--dark-border)' : undefined,
                          color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                      >
                        Temizle
                      </Button>
                    </div>
                  </Col>
                </Row>

                {tableError && (
                  <Alert
                    message="Hata"
                    description={tableError}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                <Table
                  dataSource={paginatedData}
                  scroll={{ x: 'max-content' }}
                  columns={visibleColumns.map((column, index) => {
                    const selectedMenuData = ikMenus.find(menu => menu.key === selectedMenu);
                    const hasActionButtons = selectedMenuData && selectedMenuData.actionButtons;
                    const columnKey = column.key || '';
                    const normalizedKey = columnKey.replace(/\s+/g, '_').toUpperCase();
                    let actionButtonsForColumn = null;
                    if (hasActionButtons) {
                      if (selectedMenu === 'zimmet-liste') {
                        const norm = trLower(columnKey).replace(/\s+/g, '_');
                        if (norm === 'key_demirbas_zimmet') {
                          actionButtonsForColumn = selectedMenuData.actionButtons['KEY_DEMIRBAS_ZIMMET'];
                        } else if (norm === 'key_zimmet') {
                          actionButtonsForColumn = selectedMenuData.actionButtons['KEY_ZIMMET'];
                        }
                      } else {
                        actionButtonsForColumn = selectedMenuData.actionButtons[columnKey] ||
                          selectedMenuData.actionButtons[normalizedKey] ||
                          selectedMenuData.actionButtons[columnKey.toLowerCase()] ||
                          selectedMenuData.actionButtons[columnKey.toUpperCase()];
                        if (!actionButtonsForColumn && Array.isArray(selectedMenuData.actionButtons) && index === visibleColumns.length - 1) {
                          actionButtonsForColumn = selectedMenuData.actionButtons;
                        }
                      }
                    }
                    return {
                      ...column,
                      filters: undefined,
                      filterDropdown: false,
                      dataIndex: column.key,
                      key: column.key,
                      align: 'center',
                      width: 40,
                      ellipsis: {
                        showTitle: true
                      },
                      title: (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ flex: 1, cursor: column.sortable ? 'pointer' : 'default' }} onClick={() => column.sortable && handleSort(column.key)}>
                            {column.title}
                          </span>
                          {column.filters && column.filters.length > 0 && (
                            <Dropdown
                              dropdownRender={() => getFilterDropdown(column, darkMode, handleAdvancedFilterChange, clearColumnFilter, filteredInfo)}
                              trigger={['click']}
                              placement="bottomRight"
                            >
                              <Button
                                type="text"
                                size="small"
                                style={{
                                  marginLeft: 4,
                                  color: filteredInfo[column.key] ? (darkMode ? 'var(--dark-text)' : '#1890ff') : '#ccc'
                                }}
                                className={darkMode ? 'dark-button' : ''}
                              >
                                <FilterOutlined style={{ fontSize: 12 }} />
                              </Button>
                            </Dropdown>
                          )}
                        </div>
                      ),
                      onHeaderCell: () => ({
                        onContextMenu: (e) => {
                          e.preventDefault();
                          removeColumn(column.key);
                        },
                        onClick: () => {
                          if (column.sortable) {
                            handleSort(column.key);
                          }
                        }
                      }),
                      ...(actionButtonsForColumn ? { onCell: () => ({ style: { textAlign: 'center' } }) } : {}),
                      render: (text, record) => (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', width: '100%', minHeight: 32, textAlign: 'center' }}>
                          {actionButtonsForColumn ? (
                            <ActionButtons record={record} actionButtons={actionButtonsForColumn} darkMode={darkMode} buttonLayout="vertical"/>
                          ) : (
                            <span>{column.render ? column.render(text, record) : (text === null || text === undefined || text === '' ? '-' : text)}</span>
                          )}
                        </div>
                      )
                    };
                  })}
                  loading={tableLoading}
                  rowKey={(record, index) => record.id || record.key || index}
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    current: pagination.current,
                    total: filteredAndSortedData.length,
                    pageSize: pagination.pageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} / ${total} kayıt`,
                    onChange: handlePageChange,
                    onShowSizeChange: handlePageChange,
                    pageSizeOptions: ['10', '25', '50', '100']
                  }}
                  size="small"
                  className={darkMode ? 'dark-table' : ''}
                  style={{
                    width: '100%',
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                  }}
                />
              </Card>
            </div>
          </Col>
        ) : (
          <>
            {/* Employee Distribution Chart */}
            <Col xs={24} sm={24} md={12} lg={8}>
              <Card
                className="ik-card"
                title={
                  <Space>
                    <PieChartOutlined />
                    <span>Çalışan Dağılımı</span>
                  </Space>
                }
                extra={
                  <Space>
                    <Button
                      type="text"
                      icon={<LeftOutlined />}
                      size="small"
                      onClick={prevChart}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {chartIndex + 1}/{chartTypes.length}
                    </Text>
                    <Button
                      type="text"
                      icon={<RightOutlined />}
                      size="small"
                      onClick={nextChart}
                    />
                  </Space>
                }
              >
                <div className="chart-container">
                  <div className="chart-title">{currentChart.label}</div>
                  {getChartData() ? (
                    <div className="chart-wrapper">
                      <Doughnut data={getChartData()} options={chartOptions} />
                    </div>
                  ) : (
                    <Empty description="Veri bulunamadı" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </div>
              </Card>
            </Col>

            {/* Holidays & Events */}
            <Col xs={24} sm={24} md={12} lg={8}>
              <Card
                className="ik-card"
                title={
                  <Space>
                    <span>
                      Resmi Tatiller
                    </span>
                  </Space>
                }
              >
                {holidays.length > 0 ? (
                  <Table
                    dataSource={holidays}
                    columns={holidaysColumns}
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    size="small"
                    rowKey="id"
                    className={darkMode ? 'dark-table ik-table' : 'ik-table'}
                  />
                ) : (
                  <Empty description="Yaklaşan Resmi Tatil Yok!" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Col>

            {/* Leave Requests & Birthdays */}
            <Col xs={24} sm={24} md={12} lg={8}>
              <Card
                className="ik-card"
                title={
                  <Space>
                    <GiftOutlined />
                    <span>Yaklaşan Doğum Günleri</span>
                  </Space>
                }
              >
                {birthdays.length > 0 ? (
                  <Table
                    dataSource={birthdays}
                    columns={birthdaysColumns}
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    size="small"
                    rowKey="id"
                    className={darkMode ? 'dark-table ik-table' : 'ik-table'}
                  />
                ) : (
                  <Empty description="Yaklaşan Doğum Günü Yok!" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
            </Col>

            {/* Upcoming Leaves & Advance Requests */}
            <Col xs={24} sm={24} md={8} lg={8}>
              <Card
                className="ik-card ik-card-compact"
                title={
                  <Space>
                    <CalendarOutlined />
                    <span>Yaklaşan İzinler</span>
                    {upcomingLeaves.length > 0 && (
                      <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
                        {upcomingLeaves.length}
                      </Text>
                    )}
                  </Space>
                }
                onClick={() => upcomingLeaves.length > 0 && setUpcomingLeavesModalOpen(true)}
                style={{ cursor: upcomingLeaves.length > 0 ? 'pointer' : 'default' }}
              >
                {upcomingLeaves.length === 0 && (
                  <Empty description="Yaklaşan İzin Yok!" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>

              <Modal
                title="Yaklaşan İzinler Detayları"
                open={upcomingLeavesModalOpen}
                onCancel={() => setUpcomingLeavesModalOpen(false)}
                footer={null}
                width={800}
                className="ik-modal demirbas-detail-modal"
              >
                <Table
                  dataSource={upcomingLeaves}
                  columns={upcomingLeavesColumns}
                  pagination={false}
                  size="small"
                  rowKey="id"
                  scroll={{ x: 'max-content', y: 400 }}
                />
              </Modal>
            </Col>

            <Col xs={24} sm={24} md={8} lg={8}>
              <Card
                className="ik-card ik-card-compact"
                title={
                  <Space>
                    <WalletOutlined />
                    <span>Avans Talepleri</span>
                    {advanceRequests.length > 0 && (
                      <Text strong style={{ color: '#fa8c16', fontSize: '14px' }}>
                        {advanceRequests.length}
                      </Text>
                    )}
                  </Space>
                }
                onClick={() => advanceRequests.length > 0 && setAdvanceRequestsModalOpen(true)}
                style={{ cursor: advanceRequests.length > 0 ? 'pointer' : 'default' }}
              >
                {advanceRequests.length === 0 && (
                  <Empty description="Avans Talebi Yok!" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>

              <Modal
                title="Avans Talepleri Detayları"
                open={advanceRequestsModalOpen}
                onCancel={() => setAdvanceRequestsModalOpen(false)}
                footer={null}
                width={800}
                className={darkMode ? 'dark-table ik-modal demirbas-detail-modal' : 'ik-modal demirbas-detail-modal'}
              >
                <Table
                  dataSource={advanceRequests}
                  columns={advanceRequestsColumns}
                  pagination={false}
                  size="small"
                  rowKey="id"
                  scroll={{ x: 'max-content', y: 400 }}
                />
              </Modal>
            </Col>

            <Col xs={24} sm={24} md={8} lg={8}>
              <Card
                className="ik-card ik-card-compact"
                title={
                  <Space>
                    <span>İzin Talepleri</span>
                    {leaveRequests.length > 0 && (
                      <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>
                        {leaveRequests.length}
                      </Text>
                    )}
                  </Space>
                }
                onClick={() => leaveRequests.length > 0 && setLeaveRequestsModalOpen(true)}
                style={{ cursor: leaveRequests.length > 0 ? 'pointer' : 'default' }}
              >
                {leaveRequests.length === 0 && (
                  <Empty description="Bekleyen İzin Talebi Yok!" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>
              <Modal
                title="İzin Talepleri Detayları"
                open={leaveRequestsModalOpen}
                onCancel={() => setLeaveRequestsModalOpen(false)}
                footer={null}
                width={1000}
                className={darkMode ? 'dark-table ik-modal demirbas-detail-modal' : 'ik-modal demirbas-detail-modal'}
              >
                <Table
                  dataSource={leaveRequests}
                  columns={leaveRequestsColumns}
                  pagination={false}
                  size="small"
                  rowKey="id"
                  scroll={{ x: 'max-content', y: 400 }}
                />
              </Modal>
            </Col>
          </>
        )}
      </Row>
      
      {/* Form Modal */}
      {formModalOpen && (
        <Modal
          title={modalTitle}
          open={formModalOpen}
          onCancel={closeFormModal}
          footer={null}
          destroyOnClose={true}
          width={800}
          className={darkMode ? 'dark-modal ik-modal' : 'ik-modal'}
          style={darkMode ? {
            backgroundColor: 'var(--dark-bg-secondary)',
          } : {}}
        >
          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            <FormikForm
              formId={currentFormId}
              uniqueValue={currentUniqueValue}
              uniqueField={formUniqueField}
              tableName={formTableName}
              extraData={currentFormExtraData}
              onSuccess={handleFormSuccess}
            />
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {detailModalOpen && (
        <Modal
          title={modalTitle}
          open={detailModalOpen}
          onCancel={closeDetailModal}
          footer={null}
          destroyOnClose={true}
          width={1200}
          style={{ top: 20 }}
          className={darkMode ? 'dark-modal ik-modal demirbas-detail-modal' : 'ik-modal demirbas-detail-modal'}
        >
          <div style={{ maxHeight: 600, overflowY: 'auto', fontSize: 12 }}>
            {detailError && (
              <Alert
                message="Hata"
                description={detailError}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Table
              dataSource={detailData}
              loading={detailLoading}
              rowKey={(record) => record.dosyaID || record.id || Math.random()}
              scroll={{ x: 'max-content' }}
              className={darkMode ? 'dark-table' : ''}
              size="small"
              style={{
                width: '100%',
                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} / ${total} kayıt`,
                pageSizeOptions: ['10', '25', '50', '100']
              }}
            >
              {detailData && detailData.length > 0 && Object.keys(detailData[0]).map((key, index) => {
                const totalColumns = Object.keys(detailData[0]).length;
                const isLastColumn = index === totalColumns - 1;

                return (
                  <Table.Column
                    key={key}
                    title={key}
                    dataIndex={key}
                    width={50}
                    ellipsis={{
                      showTitle: true
                    }}
                    render={(text, record) => (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isLastColumn ? (
                          <Space size="small">
                            {modalButtonType === 'main-table' ? (
                              <>
                                <Button
                                  type="default"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => {
                                    handleEditFile(record);
                                  }}
                                  className={darkMode ? 'dark-button' : ''}
                                  style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                  }}
                                >
                                  Düzenle
                                </Button>
                                <Button
                                  type="default"
                                  size="small"
                                  icon={<PlusOutlined />}
                                  onClick={() => handleAddDelivery(record)}
                                  className={darkMode ? 'dark-button' : ''}
                                  style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                  }}
                                >
                                  Teslimat Ekle
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  type="default"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={() => handleViewFile(record)}
                                  className={darkMode ? 'dark-button' : ''}
                                  style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                  }}
                                >
                                  Dosya Aç
                                </Button>
                              </>
                            )}
                          </Space>
                        ) : (
                          <span>{text}</span>
                        )}
                      </div>
                    )}
                  />
                );
              })}
            </Table>
          </div>
        </Modal>
      )}

      {/* İzin Talebi Modal */}
      <Modal
        title="İzin Talebi"
        open={izinModalOpen}
        onCancel={closeIzinModal}
        footer={izinModalData && !izinModalLoading ? (
          <Space>
            <Button
              type="default"
              className="izin-onayla-btn"
              icon={<CheckOutlined />}
              onClick={handleIzinOnayla}
              style={{
                backgroundColor: darkMode ? '#52c41a' : '#52c41a',
                borderColor: darkMode ? '#52c41a' : '#52c41a',
                color: '#fff',
              }}
            >
              Onayla
            </Button>
            <Button
              type="default"
              className="izin-reddet-btn"
              icon={<CloseOutlined />}
              onClick={handleIzinReddet}
              style={{
                backgroundColor: darkMode ? '#ff4d4f' : '#e01025',
                borderColor: darkMode ? '#ff4d4f' : '#e01025',
                color: '#fff',
              }}
            >
              Reddet
            </Button>
          </Space>
        ) : null}
        destroyOnClose
        width={640}
        className={darkMode ? 'dark-modal ik-modal' : 'ik-modal'}
      >
        {izinModalLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        )}
        {izinModalError && (
          <Alert type="error" message="Hata" description={izinModalError} showIcon style={{ marginBottom: 16 }} />
        )}
        {izinModalData && !izinModalLoading && (() => {
          const d = izinModalData;
          const startDate = d.baslangicTarihi;
          const endDate = d.bitisTarihi;
          const startTime = d.baslangicSaati || '';
          const endTime = d.bitisSaati || '';
          let izinliTarih = '-';
          if (startDate && endDate) {
            izinliTarih = startDate === endDate ? formatIzinDate(startDate) : `${formatIzinDate(startDate)} - ${formatIzinDate(endDate)}`;
          } else if (startDate) izinliTarih = formatIzinDate(startDate);
          let izinliSaatler = '-';
          const st = String(startTime || '').substring(0, 5);
          const et = String(endTime || '').substring(0, 5);
          if (st && et) izinliSaatler = `${st} - ${et}`;
          const izinSuresi = (startDate && endDate)
            ? (Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1)
            : (d.izin_suresi ?? '-');
          const yillikDusulecek = (startDate && endDate && st && et && st !== '09:00' && et !== '18:30') ? 0 : (d.yillik_izin ?? 0);
          const rows = [
            { label: 'Tarih', value: formatIzinDate(d.eklenmeTarihi) },
            { label: 'Adı Soyadı', value: d.calisan_ad_soyad ?? '-' },
            { label: 'Görev Yaptığı Ünite', value: d.departman ?? '-' },
            { label: 'İzin Tipi', value: d.izinTipi ?? '-' },
            { label: 'İzin Sebebi', value: d.izinSebebi ?? '-' },
            { label: 'İzinli Bulunacağı Tarih', value: izinliTarih },
            { label: 'İzinli Bulunacağı Saatler', value: izinliSaatler },
            { label: 'İzin Süresi', value: izinSuresi },
            { label: 'İzin Süresi (Yıllık İzinden Düşülecek)', value: yillikDusulecek }
          ];
          return (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500, width: '40%', verticalAlign: 'top', color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)' }}>{r.label}</td>
                    <td style={{ padding: '10px 12px', color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.65)' }}>{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </Modal>
    </div>
  );
};

export default IKPage;